<template>
    <div class="chart-container">
        <Chart
            v-if="hasData"
            type="line"
            :data="chartData"
            :options="chartOptions"
            class="w-full h-full"
        />
        <div v-else class="chart-empty">Sem dados</div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import SafeChart from '@/components/SafeChart.vue';

const Chart = SafeChart;

const props = defineProps({
    rawData: { type: Object, default: () => ({}) }
});

const hasData = computed(() => {
    return props.rawData?.labels?.length > 0 && props.rawData?.datasets?.length > 0;
});

const chartData = computed(() => {
    if (!hasData.value) return {};
    
    const datasets = props.rawData.datasets.map(ds => {
        return {
            ...ds,
            fill: true,
            tension: 0.4, // Bezier curve
            backgroundColor: (context) => {
                const chart = context.chart;
                const {ctx, chartArea} = chart;
                if (!chartArea) {
                    // This case happens on initial chart load
                    return null;
                }
                // Create linear gradient
                const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
                gradient.addColorStop(0, 'rgba(59, 130, 246, 0)'); // #3b82f6 with 0 opacity
                gradient.addColorStop(1, 'rgba(59, 130, 246, 0.4)'); // #3b82f6 with opacity
                return gradient;
            },
            borderColor: '#3b82f6',
            borderWidth: 2,
            pointBackgroundColor: '#ffffff',
            pointBorderColor: '#3b82f6',
            pointBorderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 6,
        };
    });

    return {
        ...props.rawData,
        datasets
    };
});

const chartOptions = computed(() => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500, easing: 'easeOutQuart' },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(20, 20, 20, 0.85)',
                titleColor: '#ffffff',
                bodyColor: '#d1d5db',
                borderColor: 'rgba(59, 130, 246, 0.3)',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                backdropFilter: 'blur(8px)',
                usePointStyle: true,
                boxPadding: 6,
            }
        },
        scales: {
            x: {
                grid: { color: 'rgba(255, 255, 255, 0.05)', drawBorder: false },
                ticks: { color: '#6b7280', font: { size: 11, family: 'Inter, sans-serif' } },
                border: { display: false }
            },
            y: {
                grid: { color: 'rgba(255, 255, 255, 0.05)', drawBorder: false },
                ticks: { color: '#6b7280', font: { size: 11, family: 'Inter, sans-serif' }, padding: 10 },
                border: { display: false },
                beginAtZero: true
            }
        }
    };
});
</script>

<style scoped>
.chart-container {
    height: 220px;
    width: 100%;
    position: relative;
}
.chart-empty {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    font-size: 0.9rem;
}
</style>
